import {
  Outlet,
  createRootRouteWithContext,
  type ErrorComponentProps,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";

import { APP_DISPLAY_NAME, COMPANY_NAME } from "../branding";
import { CraniometrixMark } from "../components/CraniometrixMark";
import { SignInNeuralBackdrop } from "../components/SignInNeuralBackdrop";
import { DASHBOARDS_INDEX_PATH, isDashboardsOnly } from "../dashboardsOnly";
import { OPERATORS_INDEX_PATH } from "../operatorsRoutes";
import { Button } from "../components/ui/button";
import { TextOutput } from "../components/TextOutput";
import { AnchoredToastProvider, ToastProvider, toastManager } from "../components/ui/toast";
import { resolveAndPersistPreferredEditor } from "../editorPreferences";
import { serverConfigQueryOptions, serverQueryKeys } from "../lib/serverReactQuery";
import { readNativeApi } from "../nativeApi";
import { useTerminalStateStore } from "../terminalStateStore";
import { terminalRunningSubprocessFromEvent } from "../terminalActivity";
import { onServerConfigUpdated } from "../wsNativeApi";
import { asThreadId } from "../lib/utils";
import { getGoogleDomainHint } from "../operators";
import {
  clearGoogleLoginHint,
  getGoogleLoginHint,
  isGoogleAutoSignInSuppressed,
  isGoogleFedCmSupported,
} from "../googleSignIn";
import { useOperatorsAuth } from "../operatorsAuth";
import { resolveRootAuthGate } from "./-rootAuthGate.logic";
import { OrchestrationSync } from "./-rootOrchestrationSync";

let googleIdentityScriptPromise: Promise<void> | null = null;

function loadGoogleIdentityScript(): Promise<void> {
  if (googleIdentityScriptPromise) {
    return googleIdentityScriptPromise;
  }

  googleIdentityScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[data-google-identity-script="true"]',
    );
    if (existingScript) {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }

      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Could not load Google Identity Services.")),
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.dataset.googleIdentityScript = "true";
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("Could not load Google Identity Services.")),
      { once: true },
    );
    document.head.appendChild(script);
  });

  return googleIdentityScriptPromise;
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  component: RootRouteView,
  errorComponent: RootRouteErrorView,
  head: () => ({
    meta: [{ name: "title", content: APP_DISPLAY_NAME }],
  }),
});

function RootRouteView() {
  const navigate = useNavigate();
  const { error, signInWithDevSession, signInWithGoogleIdToken, status } = useOperatorsAuth();
  const hasNativeApi = status === "signedIn" ? Boolean(readNativeApi()) : false;
  // Dashboards-only builds never open a socket, so there is no native API to wait for.
  const authGate = resolveRootAuthGate(status, hasNativeApi || isDashboardsOnly());
  // A dashboards preview has no Google sign-in of its own: the outer preview
  // proxy already authenticated the viewer, so sign in once automatically and
  // leave the button as the retry path if it fails.
  const autoDevSignInAttempted = useRef(false);
  useEffect(() => {
    if (authGate !== "signIn" || !isDashboardsOnly() || !import.meta.env.DEV) return;
    if (autoDevSignInAttempted.current) return;
    autoDevSignInAttempted.current = true;
    void signInWithDevSession().catch(() => undefined);
  }, [authGate, signInWithDevSession]);

  if (authGate === "boot") {
    return (
      <AppBootSplash
        message={
          status === "signedIn" ? `Connecting to ${APP_DISPLAY_NAME} server...` : "Loading..."
        }
      />
    );
  }

  if (authGate === "signIn") {
    return (
      <OperatorsSignInView
        error={error}
        isSigningIn={status === "signingIn"}
        onGoogleCredential={async (idToken) => {
          await signInWithGoogleIdToken(idToken);
          await navigate({
            to: isDashboardsOnly() ? DASHBOARDS_INDEX_PATH : OPERATORS_INDEX_PATH,
            replace: true,
          });
        }}
        onDevSignIn={async () => {
          await signInWithDevSession();
          await navigate({
            to: isDashboardsOnly() ? DASHBOARDS_INDEX_PATH : OPERATORS_INDEX_PATH,
            replace: true,
          });
        }}
      />
    );
  }

  return (
    <ToastProvider>
      <AnchoredToastProvider>
        <EventRouter />
        <DesktopProjectBootstrap />
        <Outlet />
      </AnchoredToastProvider>
    </ToastProvider>
  );
}

export function AppBootSplash({ message }: { message: string }) {
  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <div className="flex flex-1 items-center justify-center">
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}

export function OperatorsSignInView(props: {
  error: string | null;
  isSigningIn: boolean;
  onGoogleCredential: (idToken: string) => Promise<void>;
  onDevSignIn?: () => Promise<void>;
}) {
  const { error, isSigningIn, onGoogleCredential, onDevSignIn } = props;
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const [showSignInOptions, setShowSignInOptions] = useState(() => !isGoogleFedCmSupported());
  const onGoogleCredentialRef = useRef(onGoogleCredential);

  useEffect(() => {
    onGoogleCredentialRef.current = onGoogleCredential;
  }, [onGoogleCredential]);

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      return;
    }

    let cancelled = false;
    let exchangeInFlight = false;
    let googleId: GoogleAccountsId | undefined;
    const googleDomainHint = getGoogleDomainHint();
    const googleLoginHint = showSignInOptions ? null : getGoogleLoginHint();

    void loadGoogleIdentityScript()
      .then(() => {
        if (cancelled || !window.google?.accounts?.id) {
          return;
        }

        googleId = window.google.accounts.id;
        googleId.initialize({
          client_id: clientId,
          callback: (response) => {
            if (cancelled || exchangeInFlight || !response.credential) {
              return;
            }

            exchangeInFlight = true;
            googleId?.cancel();
            void onGoogleCredentialRef
              .current(response.credential)
              .catch(() => {
                // The auth provider exposes failures. Keep the button available
                // for a deliberate retry without automatically prompting again.
              })
              .finally(() => {
                exchangeInFlight = false;
              });
          },
          hd: googleDomainHint,
          ...(googleLoginHint ? { login_hint: googleLoginHint } : {}),
          // The explicit button is also the escape hatch when FedCM is
          // unavailable or blocked by browser settings. Use Google's popup flow.
          use_fedcm_for_button: false,
          button_auto_select: false,
          cancel_on_tap_outside: false,
          auto_select: !showSignInOptions && !isGoogleAutoSignInSuppressed(),
          ux_mode: "popup",
        });
        if (showSignInOptions && googleButtonRef.current) {
          googleButtonRef.current.replaceChildren();
          googleId.renderButton(googleButtonRef.current, {
            click_listener: () => googleId?.cancel(),
            theme: "outline",
            size: "large",
            text: "continue_with",
            shape: "pill",
            width: 280,
          });
        }
        if (!showSignInOptions) {
          try {
            googleId.prompt();
          } catch {
            setShowSignInOptions(true);
          }
        }
      })
      .catch(() => {
        // The explicit error panel below covers the failure case.
      });

    return () => {
      cancelled = true;
      googleId?.cancel();
    };
  }, [showSignInOptions]);

  const missingClientId =
    typeof import.meta.env.VITE_GOOGLE_CLIENT_ID !== "string" ||
    import.meta.env.VITE_GOOGLE_CLIENT_ID.length === 0;

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#ff9a3c] px-4 py-10 text-stone-900 sm:px-6">
      <SignInNeuralBackdrop />

      <section className="relative w-full max-w-md rounded-3xl bg-white/80 p-6 shadow-[0_24px_80px_-28px_rgba(194,84,12,0.45)] backdrop-blur-md sm:p-8">
        <CraniometrixMark className="h-10" />
        <p className="mt-5 text-[11px] font-semibold tracking-[0.18em] text-orange-700/70 uppercase">
          {COMPANY_NAME}
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight text-stone-900">
          Sign in to Operators
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-stone-600">
          {showSignInOptions
            ? "Sign in with your company Google account."
            : "Continue in the Google sign-in prompt using your company account."}
        </p>

        <div className="mt-6 space-y-3">
          {import.meta.env.DEV && onDevSignIn ? (
            <Button
              variant="outline"
              // This card hardcodes a light palette, so the themed outline
              // tokens go white-on-white in dark mode.
              className="w-full border-stone-300 bg-white text-stone-900 hover:bg-stone-100 hover:text-stone-900"
              disabled={isSigningIn}
              onClick={() => {
                void onDevSignIn().catch(() => {
                  // The error panel below reports the failure.
                });
              }}
            >
              Dev sign-in (no Google)
            </Button>
          ) : null}
          {missingClientId ? (
            <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">
              Missing `VITE_GOOGLE_CLIENT_ID`. Configure the UI environment before signing in.
            </div>
          ) : (
            <div>
              {!showSignInOptions ? (
                <div className="text-center text-sm text-stone-600">
                  <p>Don&apos;t see the prompt?</p>
                  <Button
                    variant="link"
                    size="sm"
                    className="mt-1 text-stone-600 underline underline-offset-4"
                    disabled={isSigningIn}
                    onClick={() => {
                      clearGoogleLoginHint();
                      setShowSignInOptions(true);
                    }}
                  >
                    More sign-in options
                  </Button>
                </div>
              ) : (
                <div ref={googleButtonRef} className="flex min-h-11 items-center justify-center" />
              )}
              {isSigningIn ? (
                <p className="mt-3 text-center text-xs text-stone-500">Completing sign-in...</p>
              ) : null}
            </div>
          )}

          {error ? (
            <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export function RootRouteErrorView({ error, reset }: ErrorComponentProps) {
  const message = errorMessage(error);
  const details = errorDetails(error);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10 text-foreground sm:px-6">
      <div className="pointer-events-none absolute inset-0 opacity-80">
        <div className="absolute inset-x-0 top-0 h-44 bg-[radial-gradient(44rem_16rem_at_top,color-mix(in_srgb,var(--color-red-500)_16%,transparent),transparent)]" />
        <div className="absolute inset-0 bg-[linear-gradient(145deg,color-mix(in_srgb,var(--background)_90%,var(--color-black))_0%,var(--background)_55%)]" />
      </div>

      <section className="relative w-full max-w-xl rounded-2xl border border-border/80 bg-card/90 p-6 shadow-2xl shadow-black/20 backdrop-blur-md sm:p-8">
        <p className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
          {APP_DISPLAY_NAME}
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
          Something went wrong.
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{message}</p>

        <div className="mt-5 flex flex-wrap gap-2">
          <Button size="sm" onClick={() => reset()}>
            Try again
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
            Reload app
          </Button>
        </div>

        <details className="group mt-5 overflow-hidden rounded-lg border border-border/70 bg-background/55">
          <summary className="cursor-pointer list-none px-3 py-2 text-xs font-medium text-muted-foreground">
            <span className="group-open:hidden">Show error details</span>
            <span className="hidden group-open:inline">Hide error details</span>
          </summary>
          <TextOutput
            title="Error details"
            copyText={details}
            contentClassName="max-h-56"
            className="rounded-none border-x-0 border-b-0"
          >
            <pre className="text-xs text-foreground/85">{details}</pre>
          </TextOutput>
        </details>
      </section>
    </div>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }

  return "An unexpected router error occurred.";
}

function errorDetails(error: unknown): string {
  if (error instanceof Error) {
    return error.stack ?? error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return "No additional error details are available.";
  }
}

function EventRouter() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const api = readNativeApi();
    if (!api) return;

    const unsubscribeTerminalEvent = api.terminal.onEvent((event) => {
      const hasRunningSubprocess = terminalRunningSubprocessFromEvent(event);
      if (hasRunningSubprocess === null) return;
      useTerminalStateStore
        .getState()
        .setTerminalActivity(asThreadId(event.threadId), event.terminalId, hasRunningSubprocess);
    });

    let subscribed = false;
    const unsubscribeServerConfigUpdated = onServerConfigUpdated((payload) => {
      void queryClient.invalidateQueries({ queryKey: serverQueryKeys.config() });
      if (!subscribed) return;
      const issue = payload.issues.find((entry) => entry.kind.startsWith("keybindings."));
      if (!issue) {
        toastManager.add({
          type: "success",
          title: "Keybindings updated",
          description: "Keybindings configuration reloaded successfully.",
        });
        return;
      }

      toastManager.add({
        type: "warning",
        title: "Invalid keybindings configuration",
        description: issue.message,
        actionProps: {
          children: "Open keybindings.json",
          onClick: () => {
            void queryClient
              .ensureQueryData(serverConfigQueryOptions())
              .then((config) => {
                const editor = resolveAndPersistPreferredEditor(config.availableEditors);
                if (!editor) throw new Error("No available editors found.");
                return api.shell.openInEditor(config.keybindingsConfigPath, editor);
              })
              .catch((error) => {
                toastManager.add({
                  type: "error",
                  title: "Unable to open keybindings file",
                  description:
                    error instanceof Error ? error.message : "Unknown error opening file.",
                });
              });
          },
        },
      });
    });
    subscribed = true;

    return () => {
      unsubscribeTerminalEvent();
      unsubscribeServerConfigUpdated();
    };
  }, [queryClient]);

  return <OrchestrationSync />;
}

function DesktopProjectBootstrap() {
  // Desktop hydration runs through EventRouter project + orchestration sync.
  return null;
}
